import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";

import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";
import { HiddenText } from "@com.mgmtp.a12.widgets/widgets-core/lib/common";
import { active, darkFocus, hover } from "@com.mgmtp.a12.widgets/widgets-core/lib/theme/base/mixins/_interaction";

import { getFVManifests } from "../app/slices/fachverfahren/selectors";
import { RESOURCE_KEYS } from "../localization";

interface ApplicationBannerProps extends React.PropsWithChildren {
	readonly interactive?: boolean;
	readonly verticalSpacing?: boolean;
}

export const ApplicationBanner: React.ComponentType<ApplicationBannerProps> = function ApplicationBanner(props) {
	return (
		<ApplicationBannerWrapper {...props}>
			<ApplicationLogo />
			<ApplicationTitle />
		</ApplicationBannerWrapper>
	);
};

const dashboardInitialActivity = {
	descriptor: {
		dashboard: "dashboard"
	},
	withoutData: true
};

const ApplicationBannerWrapper: React.ComponentType<ApplicationBannerProps> =
	function InteractiveApplicationBannerWrapper(props) {
		const dispatch = useDispatch();
		const { localizer } = React.useContext(LocalizerContext);
		const fvManifests = useSelector(getFVManifests);

		const onClick = React.useCallback(() => {
			// Wenn EmptyPortalPage angezeigt wird, Seite neu laden, um eventuelle FV zu laden
			if (fvManifests.length === 0) {
				location.reload();
				return;
			}

			// Dashboard öffnen, wenn mindestens ein FV deployt ist
			dispatch(ApplicationActions.startMainActivityRequested(dashboardInitialActivity));
		}, [fvManifests, dispatch]);

		const interactiveProps = React.useMemo<
			Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "ref">
		>(
			() =>
				props.interactive
					? {
							title: localizer({
								key: RESOURCE_KEYS.application.header.title[
									fvManifests.length === 0 ? "reload" : "backToDashboard"
								]
							}),
							onClick,
							onKeyDown(event) {
								if (event.key === "Enter") {
									onClick();
								}
							},
							tabIndex: 0,
							role: "link"
						}
					: {},
			[dispatch, props.interactive, localizer, fvManifests.length, onClick]
		);

		return React.Children.count(props.children) > 0 ? (
			<StyledApplicationBannerWrapper {...props} {...interactiveProps}>
				{props.children}
			</StyledApplicationBannerWrapper>
		) : null;
	};

const StyledApplicationBannerWrapper = styled.div<ApplicationBannerProps>`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;

	gap: calc(2 * ${({ theme }) => theme.components.applicationHeader.slot.marginRight});
	padding: ${({ theme }) => theme.components.applicationHeader.slot.marginRight};

	${({ interactive, onClick }) =>
		interactive && onClick
			? css`
					cursor: pointer;

					margin: calc(
						${({ theme }) => theme.components.applicationHeader.slot.marginRight} -
							${({ theme }) => theme.components.button.borderWidth}
					);

					border-width: ${({ theme }) => theme.components.button.borderWidth};
					border-style: solid;
					border-color: transparent;

					${active(css`
						&:not(:disabled) {
							border-color: ${({ theme }) => theme.colors.interaction.active.color};
						}
					`)}

					${hover(css`
						&:not(:disabled) {
							border-color: ${({ theme }) => theme.colors.interaction.hover.color};
						}
					`)}

					&:not(:disabled):focus {
						border-color: ${({ theme }) => theme.colors.interaction.focus.color};
						${darkFocus};
					}
				`
			: css`
					margin: ${({ theme }) => theme.components.applicationHeader.slot.marginRight};
				`}

	${({ verticalSpacing }) =>
		verticalSpacing
			? css`
					margin-top: 36px;
					margin-bottom: 36px;
				`
			: ""}
`;

const ApplicationTitle: React.ComponentType = function ApplicationTitle() {
	const { localizer } = React.useContext(LocalizerContext);

	const title = "Fachverfahrens-Portal";
	const prefix = localizer({ key: RESOURCE_KEYS.application.titleParts.prefix });
	const main = localizer({ key: RESOURCE_KEYS.application.titleParts.main });

	if (!main || !prefix) {
		return <StyledApplicationTitleMain>{title}</StyledApplicationTitleMain>;
	}

	return (
		<div>
			<StyledApplicationTitlePrefix aria-hidden>{prefix}-</StyledApplicationTitlePrefix>
			<StyledApplicationTitleMain aria-hidden>{main}</StyledApplicationTitleMain>
			<HiddenText>{title}</HiddenText>
		</div>
	);
};

export const StyledApplicationTitlePrefix = styled.div`
	font-size: 0.8rem;
	line-height: 1;
	color: ${({ theme }) => theme.components.applicationTitle.prefix.color};
`;

export const StyledApplicationTitleMain = styled.div(
	({ theme }) => css`
		text-transform: ${theme.components.applicationTitle.title.textTransform};
		font-size: 1.5rem;
		line-height: 1;
		color: ${theme.components.applicationTitle.title.color};
		margin-left: -1px;
	`
);

export const ApplicationLogo: React.ComponentType = function ApplicationLogo() {
	const { localizer } = React.useContext(LocalizerContext);
	return (
		<StyledApplicationLogo
			src="/portal/assets/images/logo.svg"
			alt={localizer({ key: RESOURCE_KEYS.application.brand })}
		/>
	);
};

const StyledApplicationLogo = styled.img`
	height: 56px;
	margin: -6px;
`;
